import { OwcIconButton, OwcListItem, OwcMenu } from "@one/react";
import { useContext, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { v4 } from "uuid";
import { useSelector } from "react-redux";
import { SubMenu } from "@digitallab/grid-common-frontend-components";
import { entryType, equipmentStatus, LOGBOOK_WRITER, LOGBOOK_ADMIN } from "../../constants";
import { EquipmentsContext } from "../equipment-context/context";
import { getFieldValue } from "../booking-instruments/helpers";
import { fetchDocumentationStatus } from "./DocumentationRender";

const checkUserGroupPermission = (irGroups, params) =>
  irGroups.some((group) => {
    const belongingToGroup = getFieldValue(params?.data, "belonging_to_group");
    return typeof belongingToGroup === "string" && belongingToGroup.startsWith(group.groupName);
  });

const REPOSITORY_URL = process.env.REACT_APP_REPOSITORY_URL || "";
const LOGBOOK_URL = process.env.REACT_APP_LOGBOOK_APP_URL || "";
const MONITORING_URL = process.env.REACT_APP_MONITORING_URL || "";

const getEntryTypeSuffix = (entryTypeValue) =>
  entryTypeValue === entryType?.cluster ? " cluster" : " equipment";

const MyLabMenuItem = ({ onClose, isInMyLab, onToggleMyLab, isDisabled }) => {
  const handleClick = () => {
    if (!isDisabled) {
      onClose();
      onToggleMyLab();
    }
  };
  return (
    <OwcListItem
      data-testid="simple-menu-my-lab"
      disabled={isDisabled}
      onClick={handleClick}
    >
      {isInMyLab ? "Remove from My lab" : "Add to My lab"}
    </OwcListItem>
  );
};

const openInNewTab = (url) => window.open(url, "_blank");

const getRepositoryUrl = (inventoryId, action) => `${REPOSITORY_URL}/equipmentList/${inventoryId}?action=${action}`;
const getLogbookCoverUrl = (inventoryId) => `${LOGBOOK_URL}/cover-sheet/${inventoryId}`;
const getLogbookLogUrl = (inventoryId, entryTypeValue, logType) => {
  const path = entryTypeValue === entryType?.cluster ? `/logs/cluster/${logType}` : `/logs/${logType}`;
  return `${LOGBOOK_URL}${path}?equipmentId=${inventoryId}`;
};

const RepositoryActionsMenuItem = ({ isEditDisabled, isRemoveDisabled, isDocumentationDisabled, entryTypeValue, inventoryId }) => (
  <SubMenu label="Repository action(s)" testId="simple-menu-repository-actions" submenuTestId="simple-menu-repository-submenu" itemCount={5}>
    {(close) => {
      const openRepoAction = (action) => {
        close();
        openInNewTab(getRepositoryUrl(inventoryId, action));
      };
      return (
        <>
          <OwcListItem
            data-testid="simple-menu-view-equipment"
            onClick={() => openRepoAction("view")}
          >
            View details
          </OwcListItem>
          <OwcListItem
            data-testid="simple-menu-edit-equipment"
            disabled={isEditDisabled}
            onClick={() => { if (!isEditDisabled) { openRepoAction("edit"); } }}
          >
            Edit{getEntryTypeSuffix(entryTypeValue)}
          </OwcListItem>
          <OwcListItem
            data-testid="simple-menu-remove-equipment"
            disabled={isRemoveDisabled}
            onClick={() => { if (!isRemoveDisabled) { openRepoAction("remove"); } }}
          >
            Remove{getEntryTypeSuffix(entryTypeValue)}
          </OwcListItem>
          <OwcListItem
            data-testid="simple-menu-view-audit-trail"
            onClick={() => openRepoAction("audit")}
          >
            View audit trail
          </OwcListItem>
          <OwcListItem
            data-testid="simple-menu-view-checklists"
            disabled={isDocumentationDisabled}
            onClick={() => { if (!isDocumentationDisabled) { openRepoAction("documentation"); } }}
          >
            View documentation
          </OwcListItem>
        </>
      );
    }}
  </SubMenu>
);

const LogbookActionsMenuItem = ({ isLogDisabled, isSubComponent, inventoryId, entryTypeValue }) => (
  <SubMenu label="Logbook action(s)" testId="simple-menu-logbook-actions" submenuTestId="simple-menu-logbook-submenu" itemCount={3}>
    {(close) => (
      <>
        <OwcListItem
          data-testid="simple-menu-view-logbook"
          onClick={() => { close(); openInNewTab(getLogbookCoverUrl(inventoryId)); }}
        >
          View Log book
        </OwcListItem>
        <OwcListItem
          data-testid="simple-menu-add-run-log"
          disabled={isLogDisabled}
          onClick={() => { if (!isLogDisabled) { close(); openInNewTab(getLogbookLogUrl(inventoryId, entryTypeValue, "run")); } }}
        >
          Add Run log(s)
        </OwcListItem>
        <OwcListItem
          data-testid="simple-menu-add-action-log"
          disabled={isLogDisabled}
          onClick={() => { if (!isLogDisabled) { close(); openInNewTab(getLogbookLogUrl(inventoryId, entryTypeValue, "action")); } }}
        >
          Add Action log(s)
        </OwcListItem>
      </>
    )}
  </SubMenu>
);

const ViewInMonitoringMenuItem = ({ onClose, inventoryId, isDisabled }) => (
  <OwcListItem
    data-testid="simple-menu-view-monitoring"
    disabled={isDisabled}
    onClick={() => { if (!isDisabled) { onClose(); openInNewTab(`${MONITORING_URL}/${inventoryId}`); } }}
  >
    View in Monitoring
  </OwcListItem>
);

const EquipmentAction = ({
  params,
  removeMyLabEquipment,
  updateMyLabEquipment,
}) => {
  const [anchorEl, setAnchorEl] = useState(false);
  const [isDocumentationDisabled, setIsDocumentationDisabled] = useState(true);
  const documentationChecked = useRef(false);
  const { myLabEquipment } = useContext(EquipmentsContext);
  const userRoles = useSelector((store) => store.user?.roles || []);
  const irRole = useSelector((store) => store.user?.irRole);
  const irGroups = useSelector((store) => store.user?.irGroups);

  const menuId = v4();
  const itemId = getFieldValue(params?.data, "id")?.toString();
  const entryTypeValue = getFieldValue(params?.data, "entry_type");
  const isSubComponent = entryTypeValue === entryType?.clusterSubequipment || !!getFieldValue(params?.data, "cluster_id");
  const isVisualized = String(getFieldValue(params?.data, "is_visualized")).toLowerCase() === "true";
  const isInMyLab = myLabEquipment.includes(itemId);

  const statusValue = getFieldValue(params?.data, "status");
  const isDeleted = statusValue === equipmentStatus.deleted.key;
  const isPending = statusValue === equipmentStatus.pending.key;

  const canCreateLog = userRoles.includes(LOGBOOK_WRITER) || userRoles.includes(LOGBOOK_ADMIN);
  const hasIrPermission = irRole === "Admin" || (irRole === "User" && checkUserGroupPermission(irGroups || [], params));
  const isRepoEditDisabled = !hasIrPermission || isDeleted || isPending;
  const isRepoRemoveDisabled = !hasIrPermission || isDeleted || isPending;
  const isLogDisabled = isSubComponent || !canCreateLog || isDeleted;

  const model = getFieldValue(params?.data, "equipment_model");
  const site = getFieldValue(params?.data, "site_name");
  const group = getFieldValue(params?.data, "belonging_to_group");

  useEffect(() => {
    if (documentationChecked.current || !model || !site || !group) {
      return;
    }
    documentationChecked.current = true;

    const fetchData = async () => {
      try {
        const items = await fetchDocumentationStatus({ model, site, group });
        setIsDocumentationDisabled(items.length === 0);
      } catch (error) {
        console.warn("Documentation check failed:", error);
      }
    };

    fetchData();
  }, [model, site, group]);

  const handleMenuClose = () => setAnchorEl(false);
  const handleMenuItemClose = () => setAnchorEl(null);
  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);

  const handleToggleMyLab = () => {
    if (isInMyLab) {
      removeMyLabEquipment(getFieldValue(params.data, "id"));
    } else {
      updateMyLabEquipment(getFieldValue(params.data, "id"));
    }
  };

  return (
    <>
      {params?.data?.id && itemId !== "dummyRow" && (
        <>
          <OwcIconButton
            icon="more_vertical"
            type="legacy"
            flat
            onClick={handleMenuClick}
            id={menuId}
          />
          {ReactDOM.createPortal(
            <OwcMenu
              data-testid="simple-menu"
              anchor={menuId}
              visible={anchorEl}
              onClickOutside={handleMenuClose}
              disablePortal={true}
            >
              {!isSubComponent && (
                <MyLabMenuItem onClose={handleMenuItemClose} isInMyLab={isInMyLab} onToggleMyLab={handleToggleMyLab} isDisabled={isDeleted} />
              )}
              <RepositoryActionsMenuItem
                isEditDisabled={isRepoEditDisabled}
                isRemoveDisabled={isRepoRemoveDisabled}
                isDocumentationDisabled={isDocumentationDisabled}
                entryTypeValue={entryTypeValue}
                inventoryId={itemId}
              />
              <LogbookActionsMenuItem isLogDisabled={isLogDisabled} isSubComponent={isSubComponent} inventoryId={itemId} entryTypeValue={entryTypeValue} />
              <ViewInMonitoringMenuItem onClose={handleMenuItemClose} inventoryId={itemId} isDisabled={!isVisualized || isDeleted} />
            </OwcMenu>,
            document.body
          )}
        </>
      )}
    </>
  );
};

export default EquipmentAction;
